/**
 * 线下门店物料管理 —— 接口层
 *
 * 需求：物料管理：线下门店物料管理
 *      https://leoao-inc.feishu.cn/wiki/Jas6w5HRziyoEkkLHv9clukOnTb
 *
 * ⚠️ 本文件是「线下门店物料管理」全模块唯一的接口收归点：
 *   - 所有端点路径统一声明在下方 REAL_API_PATHS 区，页面只 import 本文件的函数，
 *     不在页面/组件里再声明任何端点字符串或直接发 $x 请求；
 *   - 后端字段/端点有变只改本文件（REAL_API_PATHS + 对应适配层），页面代码不动；
 *   - 页面拿到的字段一律是下方「视图模型」列出的名字，不是后端原始字段。
 *
 * ── 端点分档 ─────────────────────────────────────────────
 * 全模块只挂 workwx S 面真实端点（契约见 ~/IdeaProjects/litta-workwx/api_reference/
 * 同名控制器目录下的 .md，以 SIT 分支为准）：
 *      MaterialBatchController         /material/batch/page | create | detail | topup
 *      MaterialBatchDownloadController /material/batch/download
 *      MaterialLedgerController        /material/list（已绑定物料台账，= 首页列表真实口子）
 * 默认一律直连 UMI_API_SERVER（本地联调 SIT：npm run dev:local_sit，base 即
 * sit-pub-callback.litta.cn/litta-workwx），不随 dev 自动 mock；
 * 仅显式 localStorage 开关 '__palmer_material_mock'='1' 才给本地假数据（见 USE_MOCK_REAL）。
 * 已接线：records 列表走 BATCH_PAGE、生成弹窗走 BATCH_CREATE、首页已绑定台账列表走
 * LEDGER_LIST（字段契约分别见 MaterialBatchController/{page,create}.md 与
 * MaterialLedgerController/list.md）；records 行内「下载」走 BATCH_DOWNLOAD（契约 §3.4）；
 * records 行内「补全剩余」走 BATCH_TOPUP（契约 §3.6）；绑定详情页走 BATCH_DETAIL
 * （在 B 侧 offlineMaterialRecord.js 接线，path 值与本组一致）。
 */
import $x from '@/utils/$x';

/**
 * ── workwx S 面真实端点（SIT 已部署）──
 * 统一收归声明在本文件；操作/详情页接线时从本组取 path，禁止在页面或别处再声明端点字符串。
 * 已接线：BATCH_PAGE → queryMaterialBatches（records 列表）；
 *         BATCH_CREATE → createQrcodeBatch（生成弹窗「确认生成」）；
 *         LEDGER_LIST → queryOfflineMaterialList（首页「已绑定物料台账」列表）。
 * 已接线：BATCH_DOWNLOAD → downloadBatchQrcode（records 行内「下载」，契约 §3.4）；
 *         BATCH_TOPUP → topupBatchQrcode（records 行内「补全剩余」，契约 §3.6）。
 * BATCH_DETAIL 由 B 侧 offlineMaterialRecord.js 接线（path 同值，见契约 §11）。
 */
const REAL_API_PATHS = {
  /** 物料生成批次分页（records） */
  BATCH_PAGE: '/material/batch/page',
  /** 创建物料生成批次 */
  BATCH_CREATE: '/material/batch/create',
  /** 批次概要 + 已绑定物料明细 */
  BATCH_DETAIL: '/material/batch/detail',
  /** 部分成功批次补全剩余（§3.6，幂等键 MATTOPUP:{batchNo}:{uuid}） */
  BATCH_TOPUP: '/material/batch/topup',
  /** 整批 / 勾选打包 ZIP 下载，返回 zipDownloadUrl */
  BATCH_DOWNLOAD: '/material/batch/download',
  /** 已绑定物料台账分页（首页绑定列表；契约 v1.2 §3.7 MaterialLedgerController.list） */
  LEDGER_LIST: '/material/list',
  /**
   * 单张大图 + 现取签名下载链接（§3.5，GET，query 传 code）。
   * 🔴 两条硬约束：
   *   1. 大图用响应里的 qrUrl（永久直链），不得拿本接口拼大图；
   *   2. qrcodeDownloadUrl 是 10 分钟签名链，只能点下载时现取现用、不得缓存复用（§2.6）。
   * ★2026-09-07：SIT 未部署（契约 §7.2 D6，GET/POST 全 404），单张下载已改走端侧 blob，
   *   **当前无调用方**（契约 §11.9）。函数与 path 保留：日后要恢复「文件名以服务端为准」的
   *   一级路径，在 QrcodePreviewModal#handleDownload 里把取链那步接回即可，本文件不用改。
   */
  CODE_IMAGE: '/material/code/image',
  /**
   * code → 物料类型反查（§3.8）。本期 4 个页面都不依赖，故只登记 path、不接线；
   * 后续要做「扫码识别类型」时在本文件补函数，禁止在页面里直发 $x。
   */
  CODE_TYPE: '/material/code/type',
};

/** 成功码（帕加马全站约定） */
const CODE_SUCCESS = '00000';

/** localStorage 覆盖开关：'1' 强制开 mock、'0' 强制关（走真实），不设置则按各档规则 */
const MOCK_STORAGE_KEY = '__palmer_material_mock';

const readMockFlag = () => {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(MOCK_STORAGE_KEY)
      : null;
  } catch (error) {
    // 隐私模式/禁用存储：不因读 localStorage 报错
    return null;
  }
};

const MOCK_DELAY = 300;

/**
 * 真实端点组 mock 开关：仅显式 '__palmer_material_mock'='1' 才开 ——
 * 留给本地无后端环境演示/截图用；其余情况（含 dev 默认、显式 '0'、build 产物）一律直连
 * UMI_API_SERVER 真实网关。⚠️ 已在 SIT 联调时不要再开它，否则会看到假数据误判为后端问题。
 * 首页台账列表、批次创建、批次列表三处共用此开关（本地假数据与真实数据的边界一致）。
 */
const USE_MOCK_REAL = readMockFlag() === '1';

/* -------------------------------------------------------------------------- */
/* 视图模型适配层                                                              */
/* -------------------------------------------------------------------------- */

/**
 * 台账行 → 首页列表页视图模型（字段以契约 v1.2 §3.7 为准）。
 * 页面只认下列视图模型字段名，后端契约名变了只改本函数，页面不动。
 * @param {Object} raw 后端 /material/list 返回的一条台账行
 *                     { materialType, materialTypeText, materialCode, qrUrl,
 *                       qrcodeDownloadUrl(恒 null), batchNo, tenantId, tenantName,
 *                       enterpriseName, targetPath, targetSceneName, bindUserId,
 *                       bindUserName, bindTime }
 * @return {Object} { id, materialType, materialTypeName, code, qrcodeImageUrl,
 *                    qrcodeDownloadUrl, storeName, subjectName, targetPath,
 *                    targetSceneName, binderName,
 *                    binderPhone, bindTime, batchNo, subjectId, bindUserId }
 */
const mapMaterialRow = (raw = {}) => ({
  /**
   * 行唯一键：契约无主键 id，且 §3.8 说明 code 可能 QR/NFC 各占一行（ambiguous），
   * 所以用 (materialType, materialCode) 组合键，而不是单列 materialCode。
   * 🔴 §8-A14：code 大小写敏感，本函数一律原样透传，不得 toLowerCase / 不截断。
   */
  id: raw.materialCode ? `${raw.materialType || ''}_${raw.materialCode}` : '',
  /** 'QR_CODE' | 'NFC'，取值见 constants.MATERIAL_TYPE */
  materialType: raw.materialType,
  /**
   * 物料类型中文名：用服务端 materialTypeText（★v1.2 改名，旧稿 materialTypeName 作废）；
   * 缺失时页面按 MATERIAL_TYPE_ENUM 兼底，避免前后端枚举漂移。
   */
  materialTypeName: raw.materialTypeText,
  /** 物料标识：二维码 11 位 / NFC 8 位短链码（服务端生成，前端不可编辑、不做格式拦截） */
  code: raw.materialCode || '',
  /** 二维码图直链（720×720 PNG 永久链）；NFC 行恒 null，二维码个别无图时页面退化只显 code */
  qrcodeImageUrl: raw.qrUrl || '',
  /**
   * 🔴 §3.7：台账列表里本字段**恒为 null**，列表行不给现成下载链。
   * ★2026-09-07：单张下载已不再需要它——组件直接拿 `qrUrl` 转 blob 落盘（契约 §11.9）；
   * 本字段仅兼容保留，后端一旦下发可当一级路径用（需同时传回 downloadQrcode 的 downloadUrl）。
   */
  qrcodeDownloadUrl: raw.qrcodeDownloadUrl || '',
  /** 绑定的商家门店/租户名（tenantKeyword 模糊搜索命中项也在此列） */
  storeName: raw.tenantName,
  /** 门店所属主体企业名 */
  subjectName: raw.enterpriseName,
  /**
   * 真实路径（台账接口 MaterialLedgerModel 定稿名 targetPath），形如 /pages/coach/index?id=1。
   * 🔴 不要抄成详情页那个名字：`bindPath` 只存在于批次详情
   *   MaterialCodeDetailModel（见 offlineMaterialRecord.js），台账列表 /material/list 没有这个字段，
   *   读错不会报错、只会整列静默变 '--'（2026-09-08 实错）。
   * 页面列名与视图模型同名，都用 targetPath。
   */
  targetPath: raw.targetPath,
  /**
   * 路径名称（原型要求，暂按 targetSceneName 取数，❗字段名待后端契约确认）：
   * 「真实路径」列上一行展示；后端未下发时恒 ''，页面自动不占一行。
   */
  targetSceneName: raw.targetSceneName || '',
  /** 商家端最新绑定操作人（教练花名，服务端已 join 好） */
  binderName: raw.bindUserName,
  /**
   * 🔴 原型「绑定人」列下方要求手机号，但契约 v1.2 全文未下发任何手机号字段，
   *   真实数据下恒 ''（页面自动隐藏那一行）。保留兼容取数路径，后端补字段即零改动生效；
   *   已作为差异项报给产品/后端（手机号属个人信息，还需确认脱敏与本角色查看权限）。
   */
  binderPhone: raw.bindUserPhone || raw.binderPhone || '',
  /** 商家端最新绑定操作时间（yyyy-MM-dd HH:mm:ss，服务端已格式化） */
  bindTime: raw.bindTime,
  /** 所属批次号（§3.7：QR 有值，NFC 为 null）；可用于跳批次详情 */
  batchNo: raw.batchNo,
  /** 绑定主体 ID（§3.7 tenantId）/ 绑定人用户 ID（§3.7 bindUserId，可空） */
  subjectId: raw.tenantId,
  bindUserId: raw.bindUserId || undefined,
});

/** 创建批次返回值 → 视图模型（字段以 create.md 的 MaterialBatchCreateModel 为准） */
const mapBatchCreateResult = (raw = {}) => ({
  /** 批次号：QRC+yyyyMMdd+当日自增 3 位（§3.1，如 QRC20260903001，全平台唯一）。
   *  跳记录页时要用它定位/置顶，不能假设新批次在第一页第一条。 */
  batchNo: raw.batchNo,
  /** §3.1：建完必为 0 生成中（缺省时兜底 0，与 constants.BATCH_STATUS.GENERATING 同义） */
  status: Number.isInteger(raw.status) ? raw.status : 0,
  /** 🔴 §3.1：toast 文案由服务端下发，前端直接展示、不硬编码（改文案不必发版） */
  toast: raw.toast || '',
});

/**
 * 生成批次行 → 视图模型（字段以 MaterialBatchController/page.md 为准）
 * 计数统一 Number 化，避免 mock 数字 / 真实字符串差异把页面带歪
 */
const mapBatchRow = (raw = {}) => {
  const toInt = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    id: raw.id,
    batchNo: raw.batchNo || '',
    title: raw.title || '',
    /** 0 生成中 / 1 生成成功 / 2 生成失败 / 3 部分成功 */
    status: toInt(raw.status),
    /** 服务端下发的中文状态文案，缺失时页面回退到 BATCH_STATUS_ENUM */
    statusText: raw.statusText || '',
    totalCount: toInt(raw.totalCount),
    successCount: toInt(raw.successCount),
    boundCount: toInt(raw.boundCount),
    topupRound: toInt(raw.topupRound),
    operatorName: raw.operatorName || '',
    remark: raw.remark || '',
    /** 生成时间（服务端已格式化 yyyy-MM-dd HH:mm:ss） */
    ctime: raw.ctime,
    /** 可下载：生成成功/部分成功 且成功数>0 */
    canDownload: !!raw.canDownload,
    /** 可补全剩余：仅 status=3 部分成功批次（§4 按钮矩阵） */
    canTopup: !!raw.canTopup,
    /** 可进绑定详情：失败批次不可 */
    canDetail: !!raw.canDetail,
  };
};

/* -------------------------------------------------------------------------- */
/* 对外接口                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 首页台账列表查询入参适配：页面视图模型字段名 → 后端字段名（契约 v1.2 §3.7）
 * storeKeyword→tenantKeyword、binderKeyword→bindUserKeyword、codeKeyword→materialCodeKeyword、
 * bindStartTime/bindEndTime→bindTimeStart/bindTimeEnd（服务端要 yyyy-MM-dd HH:mm:ss）、
 * materialTypes 原样透传（★v1.1 起为字符串数组 'QR_CODE'/'NFC'）。
 * 空值一律收口不发送，但 requestData 保证恒为 {}（接口 @ValidParameter dataNotNull=true，无筛选也须传 {}）。
 */
/**
 * 页面 RangePicker 只给到「日」（YYYY-MM-DD），契约要带时分秒。
 * 开始侧补 00:00:00、结束侧补 23:59:59；否则「到今天」这一整天的记录会被 BETWEEN 条件漏掉。
 * 已带时分秒的值原样透传，不二次加工。
 */
const toDayBoundary = (value, isEnd) => {
  const text = `${value ?? ''}`.trim();
  if (!text || text.length > 10) {
    return text;
  }
  return `${text} ${isEnd ? '23:59:59' : '00:00:00'}`;
};

const buildLedgerListPayload = (params = {}) => {
  const requestData = params.requestData || {};
  const request = {};
  const put = (viewKey, contractKey) => {
    const value = `${requestData[viewKey] ?? ''}`.trim();
    if (value) {
      request[contractKey] = value;
    }
  };
  put('storeKeyword', 'tenantKeyword');
  put('binderKeyword', 'bindUserKeyword');
  put('codeKeyword', 'materialCodeKeyword');
  const start = toDayBoundary(requestData.bindStartTime, false);
  const end = toDayBoundary(requestData.bindEndTime, true);
  if (start) {
    request.bindTimeStart = start;
  }
  if (end) {
    request.bindTimeEnd = end;
  }
  if (
    Array.isArray(requestData.materialTypes) &&
    requestData.materialTypes.length
  ) {
    request.materialTypes = requestData.materialTypes;
  }
  return {
    requestData: request,
    page: params.page || { currentPage: 1, pageSize: 10 },
  };
};

/**
 * 查询已绑定物料台账列表（首页，S 端全局视角，跨所有商家；只读）
 * 数据源：定稿档 REAL_API_PATHS.LEDGER_LIST = POST /material/list（契约 list.md，只回绑定态=1 且
 * 未删除的码行，排序服务端固定 bind_time desc）；仅显式 '__palmer_material_mock'='1' 时走 mock。
 * mock 与真实返回都走同一遍 mapMaterialRow，联调视图模型不漂移。
 * @param {Object} params { requestData: {storeKeyword, materialTypes, bindStartTime,
 *                          bindEndTime, binderKeyword, codeKeyword}, page: {currentPage, pageSize} }
 * @return {Promise} { code, data: 台账行[]（视图模型见 mapMaterialRow）, msg, page: { count } }
 */
export async function queryOfflineMaterialList(params) {
  const payload = buildLedgerListPayload(params);
  const res = USE_MOCK_REAL
    ? await mockMaterialLedgerPage(payload)
    : (await $x.post(REAL_API_PATHS.LEDGER_LIST, payload)) || {};
  if (res.code !== CODE_SUCCESS) {
    return res;
  }
  return {
    ...res,
    data: (res.data || []).map(mapMaterialRow),
  };
}

/**
 * 生成批次创建入参适配：页面视图模型字段名 → 后端字段名（契约 MaterialBatchController/create.md）
 * batchTitle→title、quantity→totalCount、idempotencyKey→idempotentKey；
 * title/remark 已由页面 trim、长度上限页面已拦，这里做收口 + 数值化兜底（异常置 null，让后端回 99998）。
 */
const buildBatchCreatePayload = (params = {}) => {
  const requestData = params.requestData || {};
  const quantity = Number(requestData.quantity);
  return {
    requestData: {
      title: `${requestData.batchTitle ?? ''}`.trim(),
      totalCount: Number.isFinite(quantity) ? quantity : null,
      remark: `${requestData.remark || ''}`.trim(),
      idempotentKey: requestData.idempotencyKey || '',
    },
  };
};

/**
 * 创建二维码物料生成批次（定稿档 ② REAL_API_PATHS.BATCH_CREATE，POST /material/batch/create）
 * 契约见 ~/IdeaProjects/litta-workwx/api_reference/MaterialBatchController/create.md
 * 注意：创建 ≠ 生成完成 —— 端点只落批次行(status=0) + 排队异步出图，图要等数分钟；
 *       页面成功后可携 batchNo 跳 records 页轮询 status（records 页已实现 query 定位）。
 * @param {Object} params { requestData: { batchTitle, quantity, remark, idempotencyKey } }
 *        （页面视图模型字段名；入参适配见 buildBatchCreatePayload）
 * @return {Promise} { code, msg, data: 见 mapBatchCreateResult }
 *         code≠00000 时 msg 为后端原文（参数错 99998 / 业务拒绝 300000），页面原样 toast、弹窗不关
 * 数据源：默认直连真实网关（同 queryMaterialBatches）；仅显式 '__palmer_material_mock'='1' 走 mock。
 */
export async function createQrcodeBatch(params) {
  const payload = buildBatchCreatePayload(params);
  const res = USE_MOCK_REAL
    ? await mockBatchCreate(payload)
    : (await $x.post(REAL_API_PATHS.BATCH_CREATE, payload)) || {};
  if (res.code !== CODE_SUCCESS) {
    return res;
  }
  return {
    ...res,
    data: mapBatchCreateResult(res.data || {}),
  };
}

/**
 * 物料生成批次分页（records 页，定稿档 ② REAL_API_PATHS.BATCH_PAGE）
 * @param {Object} params { requestData: { title, batchNo, status }, page: { currentPage, pageSize } }
 *                         无任何筛选时 requestData 也须传 {}（接口 @ValidParameter dataNotNull=true）
 * @return {Promise} { code, data: 批次行[], msg, page: { count } }；行字段见 mapBatchRow
 * 排序由服务端固定（ctime desc, id desc），页面不做列排序
 * 数据源：默认直连真实网关（dev:local_sit = SIT）；仅显式 '__palmer_material_mock'='1' 时
 *         走 mock（无后端演示用）。mock 与真实返回同过 mapBatchRow，联调视图模型不漂移。
 */
export async function queryMaterialBatches(params) {
  const res = USE_MOCK_REAL
    ? await mockMaterialBatchPage(params)
    : (await $x.post(REAL_API_PATHS.BATCH_PAGE, params)) || {};
  if (res.code !== CODE_SUCCESS) {
    return res;
  }
  return {
    ...res,
    data: (res.data || []).map(mapBatchRow),
  };
}

/**
 * 单张大图 + 现取下载链接（契约 v1.2 §3.5，GET /material/code/image?code=xxx）
 * ⚠️ **当前无调用方**：接口 SIT 未部署（§7.2 D6），单张下载已改走端侧 blob（§11.9）。
 *    保留本函数是为了后端部署后能一行接回一级路径；不要在这里删，也不要在页面里重新接上。
 * 🔴 若重新接线：调用时机只能在用户点「下载」那一刻，返回值不得存进 state / 列表 / 缓存复用（§2.6）。
 * @param {string} code 物料标识（= materialCode；§8-A14 大小写敏感，只 trim 不改大小写）
 * @return {Promise} { code, msg, data: { materialCode, qrUrl, qrcodeDownloadUrl, fileName, width, height } }
 */
export async function queryQrcodeImage(code) {
  const query = { code: `${code ?? ''}`.trim() };
  if (!query.code) {
    return { code: '99998', msg: '缺少物料标识', data: null };
  }
  const res = USE_MOCK_REAL
    ? await mockQrcodeImage(query)
    : (await $x.get(REAL_API_PATHS.CODE_IMAGE, query)) || {};
  if (res.code !== CODE_SUCCESS) {
    return res;
  }
  const raw = res.data || {};
  return {
    ...res,
    data: {
      materialCode: raw.materialCode || query.code,
      /** 永久直链（开大图用的就是它） */
      qrUrl: raw.qrUrl || '',
      /** 10 分钟签名直链（带 Content-Disposition），一级下载路径用它 */
      qrcodeDownloadUrl: raw.qrcodeDownloadUrl || '',
      /** 服务端生成好的文件名，直接用，前端不拼 */
      fileName: raw.fileName || '',
      width: raw.width,
      height: raw.height,
    },
  };
}

/**
 * ZIP 下载入参适配（契约 v1.2 §3.4，非分页接口 → 请求体一律包在 requestData 里）
 * 整批 vs 勾选子集的分界：
 *   - codes 不传 / 空数组 / 全空字串 → **请求体里不带 codes 字段** = 下整批次（契约：不传或传空=整批）；
 *     ⚠ 不把 `codes: []` 发出去，避开后端把空数组当成「选了 0 张」的歧义。
 *   - codes 有值 → 去空 + 去重后原样透传；🔴 不改大小写（§8-A14，NFC 码含大写），
 *     也不拦长度/格式：“code 必须属于该批次且已出图”由服务端整单校验（§7-2）。
 * @param {Object}   params
 * @param {string}   params.batchNo 批次编号（必填）
 * @param {string[]} [params.codes] 勾选的物料标识；不传 = 整批次
 * @return {Object} { requestData: { batchNo, codes? } }
 */
const buildBatchDownloadPayload = (params = {}) => {
  const requestData = { batchNo: `${params.batchNo ?? ''}`.trim() };
  const codes = Array.isArray(params.codes)
    ? params.codes.map((code) => `${code ?? ''}`.trim()).filter(Boolean)
    : [];
  const uniqueCodes = [];
  codes.forEach((code) => {
    if (uniqueCodes.indexOf(code) === -1) {
      uniqueCodes.push(code);
    }
  });
  if (uniqueCodes.length) {
    requestData.codes = uniqueCodes;
  }
  return { requestData };
};

/**
 * ZIP 下载出参 → 视图模型（字段以契约 v1.2 §3.4 为准）
 * ⚠ fileName 一律用服务端下发的（已含「第N轮 / XofY / 选中N」语义），前端不自己拼；
 *   partial 缺失时按两个计数就地补算，宁多提示不可漏提示（§3.4：“别静默让用户拿去印刷”）。
 */
const mapBatchDownloadResult = (raw = {}) => {
  const toInt = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  const entryCount = toInt(raw.entryCount);
  const expectedCount = toInt(raw.expectedCount);
  return {
    /** ZIP 直链（可能是签名链）：只能「点了就用」，不得存 state / 复用（§2.6） */
    zipDownloadUrl: raw.zipDownloadUrl || '',
    /** 服务端生成好的文件名（未给时为空，交给响应头定名） */
    fileName: raw.fileName || '',
    entryCount,
    expectedCount,
    partial:
      raw.partial === undefined || raw.partial === null
        ? entryCount !== expectedCount
        : !!raw.partial,
  };
};

/**
 * 下载整批 / 勾选子集 ZIP（定稿档 REAL_API_PATHS.BATCH_DOWNLOAD，POST /material/batch/download）
 *
 * 契约 §3.4 三条硬觿定，本函数逐条落实：
 *   1. **点了就取、取了就用**：zipDownloadUrl 不缓存、不预拉、不入列表 state（§2.6）；
 *   2. **同步返回**（100 张 ≈ 100~300KB、几秒），所以**没有轮询**，调用方只需按钮 loading + 防重；
 *   3. 非 00000（99998 参数 / 300000 业务拒）原样返回，页面直接 toast msg，不自编文案。
 *
 * @param {Object}   params { batchNo, codes? }（页面视图模型字段名，适配见 buildBatchDownloadPayload）
 * @return {Promise} { code, msg, data: 见 mapBatchDownloadResult }
 * 数据源：默认直连真实网关；仅显式 '__palmer_material_mock'='1' 时走 mock（mock 也能落盘一个空 ZIP）。
 */
export async function downloadBatchQrcode(params = {}) {
  const payload = buildBatchDownloadPayload(params);
  if (!payload.requestData.batchNo) {
    // 本地拦一道：没批次号没任何可下载的对象，不必发请求（后端同场景回 99998）
    return { code: '99998', msg: '缺少批次编号', data: null };
  }
  const res = USE_MOCK_REAL
    ? await mockBatchDownload(payload)
    : (await $x.post(REAL_API_PATHS.BATCH_DOWNLOAD, payload)) || {};
  if (res.code !== CODE_SUCCESS) {
    return res;
  }
  return {
    ...res,
    data: mapBatchDownloadResult(res.data || {}),
  };
}

/**
 * 补全剩余入参适配（契约 MaterialBatchController/topup.md，非分页接口 → 请求体一律包在 requestData 里）
 * 只有两个字段（同 buildBatchDownloadPayload 惯例）：
 *   - batchNo：本地先 trim（契约「先 trim；空直接参数错」）；
 *   - idempotentKey：由页面 genIdempotencyKey(batchNo) 生成（`MATTOPUP:{batchNo}:{uuid}`）；
 *     真实批次号 15 位 + uuid 36 位 = 61 字符，在服务端 64 上限内；降级键（非安全上下文）更短。
 *     ⚠ 不在本地拦长度：超长属代码缺陷不是用户输入，交服务端回 99998 原样 toast 才能暴露。
 */
const buildBatchTopupPayload = (params = {}) => ({
  requestData: {
    batchNo: `${params.batchNo ?? ''}`.trim(),
    idempotentKey: `${params.idempotentKey ?? ''}`.trim(),
  },
});

/**
 * 补全剩余出参 → 视图模型（字段以 topup.md 的 MaterialTopupModel 为准）
 * ⚠ need 是服务端提交瞬间算的实际缺口，可能与确认弹窗里 totalCount−successCount 的估算不一致
 *   （瞬间有新码出图/作废）——提示文案一律以本字段为准（契约 §3.6「提交后以响应 need 为准」）。
 */
const mapBatchTopupResult = (raw = {}) => {
  const toInt = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  return {
    batchNo: raw.batchNo || '',
    /** 本次补足张数（= 提交瞬间 totalCount − successCount，一次补齐全部缺口） */
    need: toInt(raw.need),
    /** 补足后的轮次（旧轮次 + 1）；上限（默认 3）由服务端判，页面不做「第 4 次」样式预判 */
    topupRound: toInt(raw.topupRound),
    /** 提交补足后批次恒回到 0（生成中）→ 调用方需刷新行，等 page/detail 出新态 */
    status: toInt(raw.status),
    /** 服务端固定提示文案（改文案不必发版），页面直接展示，本函数只负责空串兑底 */
    toast: raw.toast || '',
  };
};

/**
 * 部分成功批次补全剩余（定稿档 REAL_API_PATHS.BATCH_TOPUP，POST /material/batch/topup，契约 §3.6）
 *
 * 契约三条硬规定，本函数逐条落实：
 *   1. **补足是全量补齐缺口**，没有「补几张」参数——页面只传 batchNo + 幂等键，不要做选数 UI；
 *   2. **提交后 status 回到 0（生成中）**，调用方需刷新列表行（§3.2 轮询未接线前至少 reload 一次）；
 *   3. 99998 参数错 / 300000 业务拒（生成中勿重复提 / 已全部成功 / 轮次上限 / CAS 瞬态）
 *      msg 即终态文案，原样返回交给页面 toast，本层不自编文案、不预判拦截。
 *
 * @param {Object}   params { batchNo, idempotentKey }（幂等键生命周期见契约 §3.6：一次点击会话一把、
 *                           失败重试沿用同一把，防同一轮补足被记两次审计）
 * @return {Promise} { code, msg, data: 见 mapBatchTopupResult }
 * 数据源：默认直连真实网关；仅显式 '__palmer_material_mock'='1' 走 mock（mock 补足后行停在
 * 「生成中」，与创建 mock 同口径，不模拟异步生成完成）。
 */
export async function topupBatchQrcode(params = {}) {
  const payload = buildBatchTopupPayload(params);
  if (!payload.requestData.batchNo) {
    // 本地拦一道：没批次号没有可补足的的对象（后端同场景回 99998「批次号不能为空」）
    return { code: '99998', msg: '批次号不能为空', data: null };
  }
  if (!payload.requestData.idempotentKey) {
    // 没幂等键服务端拒绝写入审计；页面会话开始就已生成，走到这里只可能是代码缺陷
    return { code: '99998', msg: '幂等键不能为空', data: null };
  }
  const res = USE_MOCK_REAL
    ? await mockBatchTopup(payload)
    : (await $x.post(REAL_API_PATHS.BATCH_TOPUP, payload)) || {};
  if (res.code !== CODE_SUCCESS) {
    return res;
  }
  return {
    ...res,
    data: mapBatchTopupResult(res.data || {}),
  };
}

/** 成功码常量，页面判断用，避免散写 '00000' */
export { CODE_SUCCESS };

/* -------------------------------------------------------------------------- */
/* mock（仅本地自测：首页台账列表 / 批次创建 / 批次列表三处共用显式开关：本地   */
/* localStorage 里设 '__palmer_material_mock'='1' 才 mock，见 USE_MOCK_REAL）  */
/* 台账 mock 行都造在后端契约字段名上（MaterialLedgerController/list.md），     */
/* 与真实响应同过 mapMaterialRow 适配层，联调视图模型不漂移。                  */
/* 覆盖：仅二维码/仅NFC、超长门店名、无图破图退化、空绑定人/空绑定时间、        */
/* 字段名严格照契约 v1.2 §3.7（targetPath / materialTypeText / batchNo）。     */
/* 🔴 契约不下发手机号，mock 也不造，避免本地看到真实环境拿不到的数据。      */
/* -------------------------------------------------------------------------- */

/** 前 5 条特殊台账行：专门用来验异常与空值兜底（字段名 = list.md 契约） */
const MOCK_LEDGER_SPECIAL = [
  {
    materialType: 'QR_CODE',
    materialTypeText: '二维码物料',
    materialCode: 'rumnqzgol46',
    qrUrl:
      'https://litta-prd-1301594083.cos.ap-nanjing.myqcloud.com/litta-resources/litta-palmer/transfer/favicon.ico',
    // §3.7 列表里恒 null；★单张下载已不取签名链，直接拿上行 qrUrl 转 blob（契约 §11.9）
    qrcodeDownloadUrl: null,
    batchNo: 'QRC20260903001',
    tenantId: 120001,
    tenantName: '洛克公园（南京西路旗舰店）',
    enterpriseName: '上海洛克体育发展有限公司',
    targetPath: '/pages/coach/index?id=120001',
    targetSceneName: '找教练',
    bindUserId: 789,
    bindUserName: '阿坤',
    bindTime: '2026-09-02 18:20:31',
  },
  {
    materialType: 'NFC',
    materialTypeText: 'NFC物料',
    // §3.8：NFC 为 8 位 Base62、含大写。code 大小写敏感（§8-A14），mock 刻意造混合大小写
    materialCode: 'Kf9d21aB',
    // NFC 行无图是常态（契约：qrUrl 恒 null），不是数据问题
    qrUrl: null,
    qrcodeDownloadUrl: null,
    // NFC 物料非本系统批次生成 → batchNo 恒 null
    batchNo: null,
    tenantId: 120002,
    tenantName: '洛克公园（虹桥天地店）',
    enterpriseName: '上海洛克体育发展有限公司',
    targetPath: '/pages/checkin/index?shopId=120002',
    targetSceneName: '到店打卡',
    bindUserId: 790,
    bindUserName: '大树',
    bindTime: '2026-09-02 09:05:00',
  },
  {
    materialType: 'QR_CODE',
    materialTypeText: '二维码物料',
    materialCode: 'p0iuyt7re21',
    // 故意给失效地址，验证「图片加载失败退化为只显 code、不出现破图」
    qrUrl: 'https://invalid.example.com/not-exist.png',
    qrcodeDownloadUrl: null,
    batchNo: 'QRC20260828002',
    tenantId: 120003,
    tenantName: '超长店铺名称测试门店（高新区文体中心分场所二号馆）',
    enterpriseName: '',
    targetPath: '/pages/course/detail?courseId=8899001&from=material',
    targetSceneName: '课程详情',
    bindUserId: null,
    bindUserName: '',
    bindTime: '2026-08-31 23:59:59',
  },
  {
    materialType: 'NFC',
    materialTypeText: 'NFC物料',
    materialCode: 'Zz12AsdU',
    qrUrl: null,
    qrcodeDownloadUrl: null,
    batchNo: null,
    tenantId: null,
    tenantName: '星海游泳馆',
    enterpriseName: '',
    targetPath: '',
    // 未绑定：路径与路径名称都空，验列不占空行
    targetSceneName: '',
    bindUserId: null,
    bindUserName: '',
    // 空绑定时间：验列渲染兜底，且带时间区间筛选时必须被排除
    bindTime: '',
  },
  {
    materialType: 'QR_CODE',
    materialTypeText: '二维码物料',
    // 二维码物料但无图：退化为只显 code
    materialCode: 'ab12cd34ef5',
    qrUrl: null,
    qrcodeDownloadUrl: null,
    batchNo: 'QRC20260821003',
    tenantId: 120004,
    tenantName: '洛克公园（仙林金鹰店）',
    enterpriseName: '南京洛克健康管理有限公司',
    targetPath: '/pages/member/index',
    targetSceneName: '会员中心',
    bindUserId: 791,
    bindUserName: '小宇',
    bindTime: '2026-09-01 10:00:00',
  },
];

/** 二维码缩略图直链（复用后台已有 COS 资源，不是真实小程序码） */
const MOCK_LEDGER_QR =
  'https://litta-prd-1301594083.cos.ap-nanjing.myqcloud.com/litta-resources/litta-palmer/transfer/favicon.ico';

const MOCK_STORE_POOL = [
  { tenantName: '沙河天虹精品馆', enterpriseName: '北京天虹体育发展有限公司' },
  {
    tenantName: '乐刻健身（高新万科店）',
    enterpriseName: '杭州乐刻体育文化有限公司',
  },
  { tenantName: '星海游泳馆', enterpriseName: '' },
  {
    tenantName: '洛克公园（仙林金鹰店）',
    enterpriseName: '南京洛克健康管理有限公司',
  },
];

const MOCK_BINDER_POOL = [
  { bindUserName: '李四' },
  { bindUserName: '张三' },
  { bindUserName: '王五' },
  // 绑定人缺失：列内只显 '--'，不报错
  { bindUserName: '' },
];

const pad2 = (num) => (`${num}`.length < 2 ? `0${num}` : `${num}`);

/**
 * 造 25 条填充台账：5 特殊 + 25 = 30 条 / 10 条一页 = 3 页，可验翻页、筛选后总数变化。
 * 固定算法（不用 Math.random）——每次刷新数据一致，缺陷才能复现。
 * 时间从 2026-08-30 12:00:00 起往前推，全部早于上面特殊行，按绑定时间倒序时特殊行落在首页。
 */
const buildMockLedgerRows = () => {
  const rows = [];
  const base = new Date(2026, 7, 30, 12, 0, 0);
  const toStr = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(
      d.getHours(),
    )}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  // 造合规物料码（§8-A10：二维码 11 位小写字母数字；NFC 8 位 Base62 含大写）
  // 固定算法不用 Math.random，保证每次刷新一致、缺陷可复现；seed 段互不重复
  const QR_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const NFC_CHARS =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const toCode = (seed, isQrcode) => {
    const chars = isQrcode ? QR_CHARS : NFC_CHARS;
    const len = isQrcode ? 11 : 8;
    let x = ((seed + 1) * 2654435761) % 2147483647;
    let out = '';
    for (let k = 0; k < len; k += 1) {
      x = (x * 48271) % 2147483647;
      out += chars[x % chars.length];
    }
    // NFC 至少留一个大写字母，专门用来验「大小写敏感、前端不转小写」（§8-A14）
    return isQrcode || out === out.toLowerCase()
      ? out
      : `${out.slice(0, 3)}${out.slice(3).toUpperCase()}`;
  };
  const PATHS = [
    '/pages/coach/index?id=120001',
    '/pages/checkin/index?shopId=120002',
    '/pages/member/index',
    '/pages/course/detail?courseId=8899001&from=material',
  ];
  // 路径名称（targetSceneName）与 PATHS 一一对应；最后一条故意留空，验无名称时不占空行
  const SCENES = ['找教练', '到店打卡', '会员中心', ''];
  for (let i = 0; i < 25; i += 1) {
    const store = MOCK_STORE_POOL[i % MOCK_STORE_POOL.length];
    const binder = MOCK_BINDER_POOL[i % MOCK_BINDER_POOL.length];
    const isQrcode = i % 2 === 0;
    rows.push({
      materialType: isQrcode ? 'QR_CODE' : 'NFC',
      materialTypeText: isQrcode ? '二维码物料' : 'NFC物料',
      materialCode: toCode(i, isQrcode),
      // 二维码给图（i===6 故意空图，验「二维码物料但无图」退化）；NFC 恒 null
      qrUrl: isQrcode && i !== 6 ? MOCK_LEDGER_QR : null,
      qrcodeDownloadUrl: null,
      // §3.1：批次号 = QRC+yyyyMMdd+当日自增 3 位
      batchNo: isQrcode ? `QRC202608${pad2(30 - i)}${pad2((i % 9) + 1)}` : null,
      tenantId: 130000 + i,
      tenantName: store.tenantName,
      enterpriseName: store.enterpriseName,
      targetPath: PATHS[i % PATHS.length],
      targetSceneName: SCENES[i % SCENES.length],
      bindUserId: 900 + i,
      bindUserName: binder.bindUserName,
      bindTime: toStr(new Date(base.getTime() - i * 6 * 3600 * 1000)),
    });
  }
  return rows;
};

const MOCK_LEDGER_ROWS = MOCK_LEDGER_SPECIAL.concat(buildMockLedgerRows());

/** 已创建的批次标题与幂等键，用于模拟后端唯一性与幂等 */
const mockCreatedTitles = [];
const mockIdempotencyMap = {};
/** mock 期「生成弹窗新建成批次」附加行（并入 records 列表 mock，跑通 创建→跳记录 自测闭环） */
const mockExtraBatchRows = [];
/** mock 期新建成批自增序号（真实批次号/主键以服务端为准） */
let mockBatchSeq = 0;
/** mock §3.6：已提交补足的批次（batchNo → { topupRound }），overlay 回「生成中」态（见 allMockBatchRows） */
const mockTopupApplied = {};
/** mock §3.6：补足幂等键 → 首次成功结果，同键重放不二次记审计（同 mockIdempotencyMap 惯例） */
const mockTopupIdempotencyMap = {};
/** 对齐 merchant 配置 material.qrcode.topupMaxRound 默认 3（topup.md 语义要点） */
const MOCK_TOPUP_MAX_ROUND = 3;

const delay = (ms, data) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(data), ms);
  });

function mockMaterialLedgerPage(payload = {}) {
  const requestData = payload.requestData || {};
  const page = payload.page || {};
  const currentPage = Math.max(1, parseInt(page.currentPage, 10) || 1);
  const pageSize = Math.max(1, parseInt(page.pageSize, 10) || 10);

  const list = MOCK_LEDGER_ROWS.filter((row) => {
    if (
      requestData.tenantKeyword &&
      !`${row.tenantName || ''}${row.enterpriseName || ''}`.includes(
        requestData.tenantKeyword,
      )
    ) {
      return false;
    }
    if (
      Array.isArray(requestData.materialTypes) &&
      requestData.materialTypes.length &&
      !requestData.materialTypes.includes(row.materialType)
    ) {
      return false;
    }
    if (
      requestData.bindUserKeyword &&
      !(row.bindUserName || '').includes(requestData.bindUserKeyword)
    ) {
      return false;
    }
    if (
      requestData.materialCodeKeyword &&
      !(row.materialCode || '').includes(requestData.materialCodeKeyword)
    ) {
      return false;
    }
    // 时间区间用完整 yyyy-MM-dd HH:mm:ss 整串比较，贴近后端 `bind_time BETWEEN` 语义：
    // service 没把「日」补成 00:00:00 / 23:59:59 时，当天记录会被筛掉，本地即可暴露漏筛。
    // 一旦带了区间，bindTime 为空的记录必须排除（对齐 SQL 中 NULL 不参与比较）。
    if (requestData.bindTimeStart || requestData.bindTimeEnd) {
      const time = row.bindTime || '';
      if (!time) {
        return false;
      }
      if (requestData.bindTimeStart && time < requestData.bindTimeStart) {
        return false;
      }
      if (requestData.bindTimeEnd && time > requestData.bindTimeEnd) {
        return false;
      }
    }
    return true;
  });

  // 模拟后端排序：绑定时间倒序（时间相同以物料码次级键，保证翻页稳定不跳行）
  list.sort(
    (a, b) =>
      String(b.bindTime || '').localeCompare(String(a.bindTime || '')) ||
      String(b.materialCode || '').localeCompare(String(a.materialCode || '')),
  );

  return delay(MOCK_DELAY, {
    code: CODE_SUCCESS,
    msg: 'ok',
    data: list.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    page: { count: list.length },
  });
}

/**
 * mock §3.5 单张大图 + 现取签名下载链接。
 * 按 code 精确（区分大小写，同 §8-A14）在台账 mock 行里找；找到就回「签名直链 + fileName」，
 * 目的：让本地也能验一级下载路径（a[download] 直接吃带 Content-Disposition 的 URL）。
 */
function mockQrcodeImage(query = {}) {
  const row = MOCK_LEDGER_ROWS.find((item) => item.materialCode === query.code);
  if (!row || !row.qrUrl) {
    return delay(MOCK_DELAY, {
      code: '300000',
      msg: '该物料暂无二维码图片，无法下载',
      data: null,
    });
  }
  const fileName = `二维码${row.materialCode}.png`;
  return delay(MOCK_DELAY, {
    code: CODE_SUCCESS,
    msg: 'ok',
    data: {
      materialCode: row.materialCode,
      qrUrl: row.qrUrl,
      // 用 COS 的 response-content-disposition 模拟服务端 attachment 头（一级下载路径）
      qrcodeDownloadUrl: `${
        row.qrUrl
      }?response-content-disposition=${encodeURIComponent(
        `attachment; filename="${fileName}"`,
      )}`,
      fileName,
      width: 720,
      height: 720,
    },
  });
}

function mockBatchCreate(payload = {}) {
  // payload 已由 buildBatchCreatePayload 转成后端字段名（title/totalCount/remark/idempotentKey）
  const requestData = payload.requestData || {};
  // 注：不把幂等键解构成局部变量，统一走 requestData.idempotentKey 取值——
  // 仓库 eslint-scope（espree / @typescript-eslint 双 parser 均复现）对「解构出的局部变量
  // 作为成员计算键 + 函数内多处提前 return」的引用解析会误报 no-undef（本文件历史真报过 3 处）。
  const { title, totalCount, remark = '' } = requestData;

  // 参数错 / 业务拒绝的 code、文案对齐 create.md（99998 参数错 / 300000 业务拒绝），msg 即页面 toast
  if (!title) {
    return delay(MOCK_DELAY, {
      code: '99998',
      msg: '批次标题不能为空',
      data: null,
    });
  }
  if (totalCount === null || totalCount === undefined) {
    return delay(MOCK_DELAY, {
      code: '99998',
      msg: '生成数量不能为空',
      data: null,
    });
  }
  if (!Number.isInteger(totalCount) || totalCount < 1 || totalCount > 100) {
    return delay(MOCK_DELAY, {
      code: '99998',
      msg: `生成数量需在 1~100 之间（当前上限 ${100}）`,
      data: null,
    });
  }
  if (!requestData.idempotentKey) {
    return delay(MOCK_DELAY, {
      code: '99998',
      msg: '缺少幂等键，无法防止重复签发',
      data: null,
    });
  }

  // 幂等重放：同 key 直接返回首次创建结果，不产生新批次。
  // 契约 §3.1 出参没有 repeated 标记，重放与首次返回同一 batchNo + 同一 toast，
  // 页面行为本就一致（关弹窗 + 用 batchNo 跳记录页定位），所以无需区分。
  if (mockIdempotencyMap[requestData.idempotentKey]) {
    return delay(MOCK_DELAY, {
      code: CODE_SUCCESS,
      msg: 'ok',
      data: { ...mockIdempotencyMap[requestData.idempotentKey] },
    });
  }
  // 批次标题全平台唯一：mock 期新建成批 + 已建标题集合都算（真实为 300000，msg 带原批次号）
  const duplicated = mockExtraBatchRows.find((row) => row.title === title);
  if (duplicated || mockCreatedTitles.includes(title)) {
    return delay(MOCK_DELAY, {
      code: '300000',
      msg: `批次标题已存在（${
        duplicated ? duplicated.batchNo : 'mock 已建批次'
      }），请修改后重试`,
      data: null,
    });
  }

  mockBatchSeq += 1;
  const now = new Date();
  const dateStr = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(
    now.getDate(),
  )}`;
  const seq3 = `${mockBatchSeq}`.padStart(3, '0');
  const batchNo = `QRC${dateStr}${seq3}`;
  const ctime = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(
    6,
    8,
  )} ${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(
    now.getSeconds(),
  )}`;
  const result = {
    batchNo,
    batchId: 910000 + mockBatchSeq,
    // §3.1：建完必为 0 生成中（响应返回时一张码都还没出，属预期，不是 bug）
    status: 0,
    toast: '二维码物料正在生成，请耐心等候！',
  };
  // 同步进 records 页 mock 主数据（插最前），跑通「创建 → 跳记录页定位新批次」自测闭环
  mockExtraBatchRows.unshift({
    id: 910000 + mockBatchSeq,
    batchNo,
    title,
    status: 0,
    statusText: '生成中',
    totalCount,
    successCount: 0,
    boundCount: 0,
    topupRound: 0,
    operatorName: '',
    remark: `${remark}`.trim(),
    ctime,
    canDownload: false,
    canTopup: false,
    canDetail: true,
  });
  mockCreatedTitles.push(title);
  mockIdempotencyMap[requestData.idempotentKey] = result;
  return delay(MOCK_DELAY, { code: CODE_SUCCESS, msg: 'ok', data: result });
}

/* -------------------------------------------------------------------------- */
/* mock：物料生成批次列表（USE_MOCK_REAL=true 才可达：显式 localStorage '__palmer_material_mock'='1'）                              */
/* 覆盖：四态（0/1/2/3）、成功数<总数、翻页（26 条 / 10 条一页 = 3 页）、筛选   */
/* 字段与状态语义对齐 MaterialBatchController/page.md，行数据造好即走 mapBatchRow */
/* -------------------------------------------------------------------------- */

const BATCH_TITLE_POOL = [
  '线下门店物料-南京西路旗舰店',
  '暑期活动物料-虹桥天地店',
  '国庆大促物料-五角场店',
  '新店开业物料-龙湖天街店',
  '会员月活动物料-宝山万达店',
  '周年庆物料-环球港店',
];

const BATCH_OPERATOR_POOL = ['陈庆明', '王鹏', '张媛', '测试同学'];

const BATCH_STATUS_TEXT = {
  0: '生成中',
  1: '生成成功',
  2: '生成失败',
  3: '部分成功',
};

function buildMockBatchRows() {
  const rows = [];
  const base = new Date(2026, 8, 22, 15, 30, 0); // 2026-09-22 15:30:00
  const toStr = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(
      d.getHours(),
    )}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  for (let i = 1; i <= 26; i += 1) {
    const total = 50 + (i % 5) * 10;
    let status;
    if (i === 3) {
      status = 0; // 常驻一条生成中
    } else if (i === 7 || i === 18) {
      status = 3; // 常驻两条部分成功（可补全剩余）
    } else if (i === 12) {
      status = 2; // 常驻一条生成失败
    } else {
      status = [0, 1, 1, 1, 1, 3][i % 6]; // 混合分布，让筛选各态都有结果
    }
    let success = 0;
    if (status === 1) {
      success = total;
    } else if (status === 3) {
      success = total - 4 - (i % 3) * 2;
    } else if (status === 2) {
      success = Math.floor(total * 0.2);
    }
    let bound = 0;
    if (status !== 0) {
      bound = Math.min(success, Math.floor(success * (0.2 + (i % 4) * 0.2)));
    }
    let remark = '';
    if (i === 3) {
      remark = '正在生成中，预计几分钟完成，请勿重复提交';
    } else if (i === 12) {
      remark = '自动重试后仍失败，请稍后重试或联系开发排查';
    } else if (i === 18) {
      remark = '本批已补足过一次';
    }
    const t = new Date(base.getTime() - (i - 1) * 137 * 60 * 1000);
    rows.push({
      id: 903000 + i,
      batchNo: `QRC202609${pad2(18 - i)}04${pad2(i)}`,
      title: BATCH_TITLE_POOL[(i - 1) % BATCH_TITLE_POOL.length],
      status,
      statusText: BATCH_STATUS_TEXT[status],
      totalCount: total,
      successCount: success,
      boundCount: bound,
      topupRound: i === 18 ? 1 : 0,
      operatorName: BATCH_OPERATOR_POOL[i % BATCH_OPERATOR_POOL.length],
      remark,
      ctime: toStr(t),
      canDownload: (status === 1 || status === 3) && success > 0,
      canTopup: status === 3,
      canDetail: status !== 2,
    });
  }
  return rows;
}

/**
 * mock 批次行单一来源：静态 26 条 + 弹窗新建行，再叠加「已提交补足」态。
 * buildMockBatchRows 每次调用都重新生成对象，补足结果若不在这层 overlay，
 * mock 里补完一次刷新就「像没补一样」（部分成功态原地不变）。
 * 补足后的行回到 status=0（生成中）且不模拟生成完成（同创建 mock 口径），
 * can* 按 page.md 矩阵同步置灰。
 */
function allMockBatchRows() {
  return [...buildMockBatchRows(), ...mockExtraBatchRows].map((row) => {
    const applied = mockTopupApplied[row.batchNo];
    if (!applied) {
      return row;
    }
    return {
      ...row,
      status: 0,
      statusText: BATCH_STATUS_TEXT[0],
      topupRound: applied.topupRound,
      canDownload: false,
      canTopup: false,
    };
  });
}

function mockMaterialBatchPage(params = {}) {
  const requestData = params.requestData || {};
  const page = params.page || {};
  const currentPage = Math.max(1, parseInt(page.currentPage, 10) || 1);
  const pageSize = Math.max(1, parseInt(page.pageSize, 10) || 10);
  const keyword = (value) => String(value || '').trim();
  const rows = allMockBatchRows().filter((row) => {
    const title = keyword(requestData.title);
    const batchNo = keyword(requestData.batchNo);
    const { status } = requestData;
    const hitTitle = !title || row.title.includes(title);
    const hitBatchNo = !batchNo || row.batchNo.includes(batchNo);
    const hitStatus =
      status === undefined || status === null || status === ''
        ? true
        : Number(status) === row.status;
    return hitTitle && hitBatchNo && hitStatus;
  });
  return delay(MOCK_DELAY, {
    code: CODE_SUCCESS,
    msg: 'ok',
    data: rows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    page: {
      count: rows.length,
      currentPage,
      pageSize,
      pages: Math.ceil(rows.length / pageSize),
    },
  });
}

/**
 * 合法的空 ZIP（22 字节 = 只带 End-Of-Central-Directory 记，解压结果为空列表）。
 * mock 下载用它，目的是让本地也能跑完「点下载 → 拿链 → 浏览器落盘 → 文件名」整条链路，
 * 🔴 只验链路、不验内容；真实 ZIP 目录结构（二维码物料/二维码{code}.png）只能连后端验（契约 §8 冒烟）。
 */
const MOCK_EMPTY_ZIP_BASE64 = 'UEsFBgAAAAAAAAAAAAAAAAAAAAAAAA==';

/**
 * mock ZIP 的可落盘地址：浏览器里造 blob: URL，非浏览器（node 脚本）退回 data URL。
 * ⚠ 为什么不用 data URL：Chrome 禁止 data: 作为顶层导航（target=_blank 会被拦），
 *   本地演示会变成“点了没反应”的伪缺陷；blob: 同源且一定能 download。
 *   代价：blob URL 不主动释放（一个演示会话漏几百字节，可接受）。
 */
const mockZipHref = () => {
  try {
    if (
      typeof window !== 'undefined' &&
      typeof Blob === 'function' &&
      window.URL &&
      typeof window.URL.createObjectURL === 'function'
    ) {
      const binary = window.atob(MOCK_EMPTY_ZIP_BASE64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return window.URL.createObjectURL(
        new Blob([bytes], { type: 'application/zip' }),
      );
    }
  } catch (error) {
    // 造 blob 失败不阻断链路验证，退回 data URL
  }
  return `data:application/zip;base64,${MOCK_EMPTY_ZIP_BASE64}`;
};

/**
 * mock §3.4 ZIP 下载。行为照 SIT 实测样本对齐（2026-09-07）：
 *   - 批次不存在 → 300000「批次不存在或已删除」；
 *   - canDownload=false（status 0/2 或 successCount=0）→ 300000「该批次…暂不可下载」（服务端不硬拦前端置灰）；
 *   - 文件名：整批 `{batchNo}_第N轮{entry}of{total}.zip`；勾选 `{batchNo}_选中{N}.zip`
 *     ⚠ 这里用 SIT 真实形式「选中N」，契约示例写的是「选中12张」，两者已不一致（见契约 D9）；
 *   - 🔴 常驻一个 partial 样例：batchNo 以 07 结尾的部分成功批次故意少打 1 张，
 *     因为真实环境几乎造不出该态（要码签发后被作废），没它测不到强制提示分支。
 */
function mockBatchDownload(payload = {}) {
  const requestData = payload.requestData || {};
  const { batchNo, codes } = requestData;
  const fail = (code, msg) => delay(MOCK_DELAY, { code, msg, data: null });
  const row = allMockBatchRows().find((item) => item.batchNo === batchNo);
  if (!row) {
    return fail('300000', '批次不存在或已删除');
  }
  if (!((row.status === 1 || row.status === 3) && row.successCount > 0)) {
    return fail('300000', '该批次二维码尚未生成完成或已失败，暂不可下载');
  }
  const isSubset = Array.isArray(codes) && codes.length > 0;
  const expectedCount = isSubset ? codes.length : row.successCount;
  const dropOne = !isSubset && /07$/.test(batchNo) && expectedCount > 1;
  const entryCount = dropOne ? expectedCount - 1 : expectedCount;
  const round = (row.topupRound || 0) + 1;
  const fileName = isSubset
    ? `${batchNo}_选中${entryCount}.zip`
    : `${batchNo}_第${round}轮${entryCount}of${row.totalCount}.zip`;
  return delay(MOCK_DELAY, {
    code: CODE_SUCCESS,
    msg: 'ok',
    data: {
      zipDownloadUrl: mockZipHref(),
      fileName,
      entryCount,
      expectedCount,
      partial: entryCount !== expectedCount,
    },
  });
}

/**
 * mock §3.6 补全剩余。行为照 MaterialBatchController/topup.md 逐条对齐：
 *   - 批次不存在 → 300000「批次不存在或已删除」；
 *   - status=0 → 300000「该批次正在生成中，请勿重复提交」（补足后行回到 0，连点第二次即命中此支）；
 *   - status=2 → 300000「生成失败批次需人工排查（见失败原因），不支持补足」；
 *   - status=1 或缺 0 张 → 300000「全部码已生成，无需补足」；
 *   - 轮次达上限（3）→ 300000「已达补足次数上限(3)，请联系管理员人工处理」；
 *   - 同幂等键重放 → 回首次结果（不二次加轮次，对齐 merchant 审计幂等键落库语义）；
 *   - 成功 → need=total−success 一次补齐、topupRound+1、status 回 0（行停在生成中，
 *     不模拟异步生成完成——与创建 mock 同口径，够验确认弹窗 / toast / 刷新置灰分支）。
 */
function mockBatchTopup(payload = {}) {
  const requestData = payload.requestData || {};
  const { batchNo, idempotentKey } = requestData;
  const fail = (code, msg) => delay(MOCK_DELAY, { code, msg, data: null });
  if (mockTopupIdempotencyMap[idempotentKey]) {
    return delay(MOCK_DELAY, {
      code: CODE_SUCCESS,
      msg: 'ok',
      data: { ...mockTopupIdempotencyMap[idempotentKey] },
    });
  }
  const row = allMockBatchRows().find((item) => item.batchNo === batchNo);
  if (!row) {
    return fail('300000', '批次不存在或已删除');
  }
  if (row.status === 0) {
    return fail('300000', '该批次正在生成中，请勿重复提交');
  }
  if (row.status === 2) {
    return fail('300000', '生成失败批次需人工排查（见失败原因），不支持补足');
  }
  const need = row.totalCount - row.successCount;
  if (row.status === 1 || need <= 0) {
    return fail('300000', '全部码已生成，无需补足');
  }
  if ((row.topupRound || 0) >= MOCK_TOPUP_MAX_ROUND) {
    return fail(
      '300000',
      `已达补足次数上限(${MOCK_TOPUP_MAX_ROUND})，请联系管理员人工处理`,
    );
  }
  const result = {
    batchNo,
    need,
    topupRound: (row.topupRound || 0) + 1,
    status: 0,
    toast: `已提交补足 ${need} 个二维码，请稍后刷新查看。`,
  };
  mockTopupApplied[batchNo] = { topupRound: result.topupRound };
  mockTopupIdempotencyMap[idempotentKey] = result;
  return delay(MOCK_DELAY, { code: CODE_SUCCESS, msg: 'ok', data: result });
}
